import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  MutableRefObject,
} from "react";
import { createPortal } from "react-dom";
import { debounce, trimEnd } from "lodash";
import { createEditor, nodeFromHTML } from "prosekit/core";
import { ProseKit, useDocChange } from "prosekit/react";
import "./prosekit.css";
import { defineExtension } from "./extension";
import { htmlFromMarkdown, markdownFromHTML } from "./markdown";
import Toolbar from "./Toolbar";
import { EditorContextProvider, EditorContextValue } from "./EditorContext";
import TagSelect from "./TagSelect";
import MentionSelect from "./MentionSelect";
import classNames from "classnames";

type EditorProps = {
  placeholder?: string | undefined;
  enableTixxtMentions?: boolean;
  onChange?(...args: unknown[]): unknown;
  onFocus?(...args: unknown[]): unknown;
  onBlur?(...args: unknown[]): unknown;
  value?: string;
  name?: string;
  readOnly?: boolean;
  id?: string;
  editorRef?: MutableRefObject<any>;
  className?: string;
  /**
   * Enables a taller, freely resizable editing area: the content grows with the
   * text up to a cap and a drag handle lets the user adjust the height. Used by
   * the activity composer ("Beitragseditor"). Off by default so comment/reply
   * and other embedded editors keep their compact, fixed-height behavior.
   */
  resizable?: boolean;
};

// Silence React v18 warnings for v19 code
const oldConsoleError = console.error;
console.error = function (...data: any[]) {
  try {
    if (
      data[0].includes("Unexpected return value from a callback ref") &&
      data[1].includes("prosekit")
    ) {
      return;
    }
    oldConsoleError.apply(console, data);
  } catch (e) {
    oldConsoleError.apply(console, data);
  }
};

// Default export
const MarkdownEditor = React.memo(function Editor({
  placeholder,
  enableTixxtMentions,
  value,
  onChange,
  readOnly,
  id,
  editorRef,
  className,
  resizable,
}: EditorProps) {
  const valueRef = useRef<string | null | undefined>(null);

  // Create the editor ONCE with its initial content (only readOnly/placeholder
  // changes rebuild it). It used to be created empty and then RECREATED once
  // the value arrived — anyone focusing the first instance in that window kept
  // an open keyboard on a thrown-away editor: no caret, typing went nowhere
  // (meinBDB #210). External value changes are applied in-place below instead.
  const editor = useMemo(() => {
    const extension = defineExtension({
      readOnly,
      placeholder,
    });
    valueRef.current = value ?? null;
    return createEditor({
      extension,
      defaultContent: value ? htmlFromMarkdown(value) : undefined,
    });
    // value is deliberately not a dependency — see comment above
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, placeholder]);
  if (editorRef) editorRef.current = editor;

  // apply external value changes in-place, keeping the editor instance (and
  // with it focus and caret) alive
  useEffect(() => {
    if (valueRef.current !== value) {
      valueRef.current = value;
      const doc = nodeFromHTML(value ? htmlFromMarkdown(value) : "<p></p>", {
        schema: editor.schema,
      });
      // markdown that only round-trips differently produces the same doc —
      // rebuilding the state would move the caret for nothing
      if (doc.eq(editor.state.doc)) return;
      // "end" instead of prosekit's default "start": when a late value lands
      // in an already focused editor (the #210 flow), typing has to continue
      // behind the restored text, not in front of it
      editor.setContent(doc, "end");
    }
  }, [value, editor]);

  // fire onChange debounced
  const debouncedOnChange = useMemo(
    () =>
      debounce(
        () => {
          const html = editor.getDocHTML();
          const markdown = trimEnd(markdownFromHTML(html));

          valueRef.current = markdown;
          onChange && onChange(markdown);
        },
        300,
        { trailing: true },
      ),
    [editor, onChange],
  );
  useDocChange(debouncedOnChange, { editor });

  // commit a still-pending change when the editor goes away (unmount or
  // rebuild) instead of letting the stale timer fire against a dead instance
  useEffect(() => () => debouncedOnChange.flush(), [debouncedOnChange]);

  // Focus editor on label click
  useEffect(() => {
    if (id) {
      const handleClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (target.getAttribute("for") === id) {
          editor.focus();
        }
      };

      document.addEventListener("click", handleClick);

      return () => {
        document.removeEventListener("click", handleClick);
      };
    }
  }, [id, editor]);

  const context = useMemo(() => ({ readOnly: readOnly || false }), [readOnly]);

  return (
    <MemoizedInnerEditor
      context={context}
      editor={editor}
      id={id}
      className={className}
      onBlur={debouncedOnChange.flush} // important when submit is clicked quickly after entering text
      enableTixxtMentions={enableTixxtMentions}
      resizable={resizable}
    />
  );
});

export default MarkdownEditor;

type MemoizedInnerEditorProps = {
  context: EditorContextValue;
  editor: ReturnType<typeof createEditor>;
  id?: string;
  className?: string;
  onBlur?: () => void;
  enableTixxtMentions?: boolean;
  resizable?: boolean;
};

/**
 * Keeps the resizable composer growing with its content *after* a manual
 * resize. The native CSS `resize` handle writes an inline `height`, which pins
 * the element and defeats the content-driven auto-grow (see #48 test feedback:
 * once the handle was dragged, typing no longer enlarged the editor, only a
 * scrollbar appeared).
 *
 * This turns a finished drag into an adjustable *floor* instead: on release, if
 * the content fits, it moves the dragged height to `min-height` and clears the
 * inline `height`, so the editor resumes growing with the text up to its CSS
 * `max-height` (then scrolls). If the user deliberately dragged the editor
 * *shorter than its content*, the dragged height is kept as a fixed `height`
 * (with scrolling) instead, so it doesn't immediately snap back over the text.
 *
 * To keep shrinking possible, the floor is dropped again at the start of the
 * next drag (pointer-down in the bottom-right handle area) while the current
 * height is pinned, so the handle moves freely without a visible snap.
 */
function useResizableAutogrow(
  ref: MutableRefObject<HTMLDivElement | null>,
  enabled: boolean,
) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled) return;

    // Approximate hit area (px) for the native resize handle in the
    // bottom-right corner. This only decides whether we proactively unpin the
    // committed floor so the drag can shrink freely; a native resize whose grab
    // slips past it is still reconciled on pointer-up (see below), so the fix
    // can't silently regress if the browser's grabber geometry differs.
    const HANDLE = 20;
    let drag: {
      inHandle: boolean;
      startHeight: number;
      startInlineHeight: string;
      prevMinHeight: string;
    } | null = null;

    // Fold the inline `height` a finished resize left behind back into the
    // auto-grow model: if the content fits, commit the dragged height as an
    // adjustable min-height *floor* and clear the inline height so the editor
    // keeps growing with the text up to the CSS max-height; if the user dragged
    // *shorter than the content*, keep the fixed height (with scrolling) instead
    // of snapping back over the text. Measured while the inline height is still
    // applied.
    const reconcile = () => {
      const height = el.getBoundingClientRect().height;
      const contentOverflows = el.scrollHeight > Math.round(height) + 1;
      if (contentOverflows) {
        el.style.minHeight = "";
        el.style.height = `${height}px`;
      } else {
        el.style.height = "";
        el.style.minHeight = `${height}px`;
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      // Only the primary button starts a resize; ignore right/middle clicks.
      if (event.button !== 0) return;
      const rect = el.getBoundingClientRect();
      const inHandle =
        event.clientX >= rect.right - HANDLE &&
        event.clientY >= rect.bottom - HANDLE;
      drag = {
        inHandle,
        startHeight: rect.height,
        startInlineHeight: el.style.height,
        prevMinHeight: el.style.minHeight,
      };
      if (inHandle) {
        // Pin the current height and drop the committed floor so this drag can
        // freely shrink (down to the CSS min) or grow, with no visible jump.
        el.style.height = `${rect.height}px`;
        el.style.minHeight = "";
      }
    };

    const onPointerUp = () => {
      if (!drag) return;
      const { inHandle, startHeight, startInlineHeight, prevMinHeight } = drag;
      drag = null;

      if (inHandle) {
        // A click / no-op drag on the handle: revert the pin exactly, leaving
        // any prior floor or kept fixed height as it was.
        if (Math.abs(el.getBoundingClientRect().height - startHeight) < 1) {
          el.style.height = startInlineHeight;
          el.style.minHeight = prevMinHeight;
          return;
        }
      } else if (el.style.height === startInlineHeight) {
        // Pointer-down outside the handle that never touched the inline height:
        // a normal click/selection, not a resize — don't disturb a kept height.
        return;
      }
      // A real resize happened — via the handle, or a native grab that slipped
      // past the hit-area (the robustness fallback).
      reconcile();
    };

    el.addEventListener("pointerdown", onPointerDown);
    // The pointer is usually released outside the tiny handle area, so listen
    // on window; pointercancel ends an interrupted drag the same way.
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
    };
  }, [ref, enabled]);
}

// This is a nice performance improvement, since we make sure to change the props only when necessary
// If this was inlined in the <Editor />, prosemirror would still be remounted on every change
const MemoizedInnerEditor = React.memo(function MemoizedInnerEditor({
  context,
  editor,
  id,
  className,
  onBlur,
  enableTixxtMentions,
  resizable,
}: MemoizedInnerEditorProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const hasToggledFullscreen = useRef(false);

  // Let the resizable composer keep growing with its content after a manual
  // resize (only active for the enlarged, non-fullscreen layout).
  useResizableAutogrow(scrollRef, !!resizable && !isFullscreen);

  // Re-focus editor after fullscreen toggle (editor is remounted in new location)
  useEffect(() => {
    if (hasToggledFullscreen.current) {
      requestAnimationFrame(() => editor.focus());
    }
    hasToggledFullscreen.current = true;
  }, [isFullscreen, editor]);

  // Enhanced context with fullscreen state and toggle function
  const enhancedContext = useMemo(() => {
    const toggleFullscreen = () => {
      const newFullscreenState = !isFullscreen;
      setIsFullscreen(newFullscreenState);

      if (newFullscreenState) {
        dialogRef.current?.showModal();
        handleDialogOpen();
      } else {
        dialogRef.current?.close();
      }
    };

    return {
      ...context,
      isFullscreen,
      toggleFullscreen,
    };
  }, [context, isFullscreen]);

  function handleDialogOpen() {
    setIsFullscreen(true);
    document.body.style.setProperty("overflow", "hidden");
  }

  function handleDialogClose() {
    setIsFullscreen(false);
    document.body.style.removeProperty("overflow");
  }

  // Editor content to render
  const editorContent = (
    <div
      className={`form-control p-0 box-border h-full w-full grow overflow-y-hidden overflow-x-hidden flex flex-col ${
        className || ""
      }`}
    >
      <Toolbar />
      <div
        ref={scrollRef}
        className={classNames("relative w-full box-border", {
          "bg-gray-100": context.readOnly,
          // Resizable composer: taller default, grows with content up to a cap
          // and exposes a native drag handle to freely adjust the height.
          "flex flex-col overflow-y-auto resize-y min-h-72 max-h-[40rem]":
            resizable && !isFullscreen,
          // Default (comments, embedded editors, fullscreen): compact, fixed.
          "flex-1 overflow-y-scroll": !(resizable && !isFullscreen),
        })}
        onMouseDown={(event) => {
          // the contenteditable may be shorter than this scroll container
          // (min-h-36 vs. the app's h-90 field) — taps into the space below
          // it should focus the editor instead of doing nothing. App-only:
          // on the web the zone doesn't exist and the handler would swallow
          // scrollbar clicks.
          if (
            window.isApp &&
            event.target === event.currentTarget &&
            !context.readOnly
          ) {
            event.preventDefault();
            editor.focus();
          }
        }}
      >
        <div
          ref={editor.mount}
          id={id}
          className={classNames(
            "ProseMirror prose box-border px-3 py-2 outline-hidden outline-0",
            {
              "text-lg": window.isApp,
              "min-h-full": isFullscreen,
              // Fill the (resizable) scroll area so the whole height is editable.
              grow: resizable && !isFullscreen,
              // Legacy compact behavior: own min/max height, parent scrolls.
              "min-h-36 max-h-96": !resizable && !isFullscreen,
            },
          )}
          onBlur={onBlur}
        />
        {enableTixxtMentions ? (
          <>
            <MentionSelect />
            <TagSelect />
          </>
        ) : null}
      </div>
    </div>
  );

  return (
    <EditorContextProvider value={enhancedContext}>
      <ProseKit editor={editor}>
        {createPortal(
          <dialog
            ref={dialogRef}
            className="tixxt__dialog inset-8 w-auto h-auto rounded m-0 p-0"
            // close when clicking on the backdrop
            onMouseDown={(event) => {
              if (event.target === event.currentTarget) {
                event.currentTarget.close();
              }
            }}
            onClose={handleDialogClose}
          >
            {isFullscreen ? editorContent : null}
          </dialog>,
          document.body,
        )}
        {!isFullscreen ? editorContent : null}
      </ProseKit>
    </EditorContextProvider>
  );
});
